import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { getErrorMsg, getDateStr, clearObj } from '../../lib/utils';
import { fields } from '../../data/fields';
import { getItemHeaderData } from '../../data/helper';
import { convertValueToImperial, metricUnitKeys, Unit } from '../../lib/units';
import { isImperial, metricsName } from '../../features/metrics/metricsSlice';
import { useAppSelector } from '../../app/hooks';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Logo from '../atoms/Logo';
import Button from '../atoms/Button';
import Offset from '../organisms/Offset';
import ItemHeaderDisplay from '../molecules/ItemHeaderDisplay';
import Form from '../molecules/Form';
import Upload from '../molecules/Upload';
import Input from '../atoms/Input';
import styles from '../../styles/components/organisms/RequestForm.module.scss';
import { createRequest } from '../../lib/apis/rfp';
import RequestSentModal from '../molecules/RequestSentModal';
import { convertParamToBoolean } from '../../graphql/queries/utils';

interface RequestFormProps {
  data: any;
  type: string;
  onSubmit: (id?: string) => void;
}

const RequestForm = (props: RequestFormProps) => {
  const { t } = useTranslation(['common', 'request']);
  const imperial = useAppSelector(isImperial);
  const unitsType = useAppSelector(metricsName);
  const [complete, setComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState() as any;
  const [formData, setFormData] = useState({}) as any;
  const [files, setFiles] = useState<File[]>([]);
  const [fieldErrors, setFieldErrors] = useState([]) as any;
  const { data, type, onSubmit } = props;
  const headerData = getItemHeaderData(data);

  const properties =
    type === 'service'
      ? []
      : [
          'event_name',
          'event_start',
          'event_end',
          'event_pax',
          'event_space',
        ].map((key: string) => {
          const defaults = fields[key];
          const value =
            formData[key] ||
            (formData[key] === false ? false : defaults.value || '');
          const { maxLength, exp } = defaults;
          const metricUnitKey =
            metricUnitKeys[unitsType][defaults.unit as Unit];
          const unit = t(`common:${metricUnitKey || defaults.unit}`);
          const placeholder =
            maxLength && !!exp
              ? t(`common:${exp}`, { maxLength })
              : typeof exp === 'number'
              ? `${t(`common:e.g.`)} ${exp}`
              : !!exp
              ? t(`common:${exp}`)
              : '';
          const fieldObj = {
            ...defaults,
            id: key,
            unit,
            label: t(`request:${key}`, { unit }),
            info:
              key === 'event_space' ? t(`request:${key}_info`, { unit }) : null,
            convertTo: !!metricUnitKey && imperial ? defaults.unit : '',
            placeholder,
            value: !formData[key]
              ? defaults.value
              : imperial && !!metricUnitKey
              ? convertValueToImperial(value, defaults.unit)
              : value,
          };
          return fieldObj;
        });

  /**
   * Handle exceptions on form change
   */
  const handleChange = (obj: any) => {
    setFieldErrors([
      ...fieldErrors.filter((str: string) => str !== 'event-end'),
    ]);
    setFormData({
      ...obj,
      event_end: formData.event_end || getDefaultEndDate(obj),
    });
    setError(false);
  };

  const getDefaultEndDate = (obj: any) => {
    return !!obj.event_start &&
      (!formData.event_end ||
        new Date(formData.event_start) > new Date(formData.event_end))
      ? getDateStr(formData.event_start)
      : '';
  };

  /**
   * Handle form errors & submit
   */
  const handleError = (error: any) => {
    const errorMsg = getErrorMsg(error);
    switch (errorMsg) {
      default:
        setError(t(`common:form-error`) + ` (${errorMsg})`);
        break;
    }
  };

  const submit = async (formData: any) => {
    if (!!fieldErrors.length) {
      console.log('setError', { fieldErrors });
      setError(t(`common:form-input-error`));
      return;
    }

    setError(false);
    setLoading(true);
    const payload = clearObj({
      ...formData,
      event_description: undefined,
      description: formData.event_description,
      files,
    });

    try {
      await createRequest(data.id, payload, files);
      setComplete(true);
      // const skipModal = localStorage.getItem('vs-upgrade-helper');
      // if (!!skipModal) {
      //   onSubmit();
      // } else {
      //   setComplete(true);
      // }
    } catch (err: any) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {complete ? (
        <Container>
          <Row className="justify-content-center">
            <Col xs={6} md={4} lg={2}>
              <Logo noLink large center />
            </Col>
          </Row>
          <Row>
            <Col xs={4} className={'offset-2'} style={{ textAlign: 'center' }}>
              <br />
              <h2>{t('request:h2-sent')}</h2>
              <p style={{ whiteSpace: 'pre' }}>
                {t('request:request-sent', { itemName: headerData.name })}
              </p>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col xs={6} md={4} lg={2}>
              <br />
              <Button
                dark
                large
                full
                label={t('common:cta-continue')}
                onClick={() => onSubmit()}
              />
              <br />
              <br />
              {/* <Checkbox
              name="showOnce"
              label={t('common:show-once')}
              onChange={(obj) => {
                obj.showOnce
                  ? localStorage.setItem('vs-upgrade-helper', 'seen')
                  : localStorage.removeItem('vs-upgrade-helper');
              }}
            /> */}
            </Col>
          </Row>
        </Container>
      ) : (
        <Form
          className={`${styles.requestForm} container`}
          checkOnce
          id={'rfpForm'}
          error={error}
          loading={loading}
          onSubmit={submit}
          submitIcon={'send'}
          submitLabel={t(`request:cta-submit`)}
          onFieldError={(arr) => {
            setFieldErrors(arr);
          }}
          onChange={handleChange}
        >
          <Row>
            <Col>
              <h2>{t(`request:headline`)}</h2>
              <br />
            </Col>
          </Row>
          <ItemHeaderDisplay
            type={type}
            data={{ ...headerData, memberships: [] }}
          />
          <Row>
            <Col>
              <br />
              <br />
              <h2>{t(`request:about-yourself`)}</h2>
              <small>{t(`request:about-yourself-required`)}</small>
            </Col>
            <Col xs={8}>
              <Offset>
                {['name', 'family_name', 'email', 'phone', 'company'].map(
                  (key, i) => (
                    <Input
                      reset
                      half
                      key={i}
                      name={key}
                      format={key}
                      icon={key}
                      label={
                        key === 'name'
                          ? t(`common:firstname`)
                          : t(`common:${key}`)
                      }
                      error={fieldErrors.indexOf(key) > -1}
                      placeholder={
                        key === 'phone'
                          ? t(`common:input-phone-exp`, {
                              countryCode: '49',
                            })
                          : ''
                      }
                      unit={key === 'phone' ? t(`common:input-phone-help`) : ''}
                    />
                  )
                )}
              </Offset>
            </Col>
            <Col>
              <br />
              <br />
              <br />
            </Col>
          </Row>
          {!!properties.length && (
            <Row>
              <Col>
                <h2>{t(`request:about-event`)}</h2>
                <small>{t(`request:about-event-required`)}</small>
              </Col>
              <Col xs={8}>
                <Offset>
                  {properties.map((el: any, i: number) => (
                    <Input
                      key={i}
                      float
                      medium
                      reset={el.type !== 'date'}
                      optional={
                        el.id === 'event_space' || el.id === 'event_end'
                      }
                      info={el.info}
                      half={el.type !== 'date'}
                      name={el.id}
                      icon={el.id}
                      label={el.label}
                      type={el.type}
                      format={el.format}
                      unit={el.unit}
                      convertTo={el.convertTo}
                      placeholder={el.placeholder}
                      maxLength={el.maxLength}
                      error={
                        el.id === 'event_end'
                          ? !!formData.event_start && !formData.event_end
                          : fieldErrors.indexOf(el.id) > -1
                      }
                      disabled={el.id === 'event_end' && !formData.event_start}
                      min={
                        el.type !== 'date'
                          ? null
                          : el.id === 'event_start'
                          ? getDateStr('', 7)
                          : el.id === 'event_end'
                          ? getDateStr(formData.event_start)
                          : undefined
                      }
                      max={
                        el.type !== 'date'
                          ? null
                          : el.id === 'event_start'
                          ? getDateStr('', 365 * 3)
                          : el.id === 'event_end'
                          ? getDateStr('', 365 * 4)
                          : undefined
                      }
                      defaultValue={
                        el.id === 'event_end' && !!formData.event_start
                          ? getDefaultEndDate(formData)
                          : el.value
                      }
                    />
                  ))}
                </Offset>
              </Col>
              <Col>
                <br />
                <br />
                <br />
              </Col>
            </Row>
          )}
          <Row>
            <Col>
              <h2>
                {type === 'service'
                  ? t(`request:describe-requirements`)
                  : t(`request:describe-event`)}
              </h2>
              <small>{t(`request:describe-event-required`)}</small>
            </Col>
            <Col xs={8}>
              <Offset>
                {['event_description'].map((key, i) => (
                  <Input
                    reset
                    full
                    grey
                    key={i}
                    type="textarea"
                    name={key}
                    format={key}
                    icon={key}
                    label={t(`common:${key}`)}
                    error={fieldErrors.indexOf(key) > -1}
                  />
                ))}
              </Offset>
            </Col>
            <Col>
              <br />
              <br />
              <br />
            </Col>
          </Row>
          <Row>
            <Col>
              <h2>{t(`request:upload`)}</h2>
              <small>{t(`request:upload-required`)}</small>
            </Col>
            <Col xs={8}>
              <Offset>
                <Upload
                  dragArea={true}
                  full
                  multiple
                  name="rfp-upload"
                  onChange={setFiles}
                />
              </Offset>
            </Col>
          </Row>
        </Form>
      )}

      <br />
      <br />
      <br />
    </>
  );
};

export default RequestForm;
